import { generalAppVariables } from '@/constants';
import {
  CloudProvider,
  ConfigurableFeature,
  ConfigurableProvider,
  ConfigurableRegion,
  ConfigurableSize,
  FeatureStatus,
  PaymentPeriod,
  PaymentVendor,
  Plan,
  ServerCreationPayload,
  ServerCreationState,
} from '@/utils/server-creation/types/server-creation-types';
import { reactive } from '@vue/composition-api';
import { defineStore } from 'pinia';

// The backend may return multiple versioned copies of the same product (e.g. v2/v3/v4
// of a "Tier 5K" plan). When the V3_PLAN_FILTER_ENABLED flag is on we only surface the
// v3 variant. Items WITHOUT a `code` are kept as-is — only items that carry a `code`
// must start with "v3" to be shown.
function hasV3Code(item: { code?: string } | null | undefined): boolean {
  const code = item?.code;
  if (!code) {
    return true;
  }
  return code.startsWith('v3');
}

export function filterV3<T extends { code?: string }>(list: T[] | undefined | null): T[] {
  if (!generalAppVariables.V3_PLAN_FILTER_ENABLED) {
    return list || [];
  }
  return (list || []).filter(hasV3Code);
}

// Plan codes that are explicitly hidden from the UI everywhere, regardless of
// the v3 filter above. The hardcoded list lives in constants (HIDDEN_PLAN_CODES).
function isHiddenPlanCode(item: { code?: string } | null | undefined): boolean {
  const code = item?.code;
  if (!code) {
    return false;
  }
  return generalAppVariables.HIDDEN_PLAN_CODES.includes(code);
}

// Feature codes are version-prefixed in v3 (e.g. "v3_daily", "v3_support-standard").
// Match by the semantic suffix so the same lookup works for both the legacy bare code
// ("daily") and the v3-prefixed code ("v3_daily").
export function featureCodeMatches(featureCode: string | undefined, code: string): boolean {
  if (!featureCode) {
    return false;
  }
  return featureCode === code || featureCode.endsWith(`_${code}`);
}

export const useServerCreationStore = defineStore('serverCreation', () => {
  // app-wide server creation state which hold the values for server creation
  // and price calculation endpoints
  // here is the single source of the truth for the server creation process
  const serverCreationState: { data: ServerCreationState } = reactive({
    data: {
      name: '',
      subscription: {
        _id: '',
        planCode: '',
        draftId: '',
        planId: '',
        planConfig: {
          region: '',
          features: [] as string[],
          bundles: [] as string[],
          size: '',
          provider: generalAppVariables.DEFAULT_PROVIDER as CloudProvider,
        },
        specialFeatures: {
          domain: '',
          backup: '',
          retention: '',
        },
        providerId: '',
      },
      activePaymentPeriod: PaymentPeriod.MONTH,
      pricing: {
        total: 0,
        currency: 'USD',
        isDiscountApplied: false,
        discountCode: '',
        discountedTotal: {
          subtotal: 0,
          tax: 0,
          total: 0,
          discount: 0,
          credit: 0,
          balance: 0,
          currency: 'USD',
        },
      },
      isLoading: true,
      provisionSummary: {
        dataToVendor: {
          company: '',
          deployment: '',
          paymentMethod: '',
          subscription: '',
        },
        items: [],
        total: {
          amount: '0',
          currency: 'USD',
        },
      },
      inProgress: false,
      isActiveServerFree: false,
      serverId: '',
      supportLevel: 'standard',
      oneTimeServices: [] as string[],
    },
  });
  // configurables like features, providers, regions and sizes will be fetched once and
  // stored here to be used in the server creation process
  const configurables: { data: {
    features: ConfigurableFeature[],
    providers: ConfigurableProvider[],
    regions: ConfigurableRegion[],
    sizes: ConfigurableSize[],
    bundles?: { _id: string; code: string; name: string; features: string[] }[],
  } } = reactive({
    data: {
      features: [],
      providers: [],
      regions: [],
      sizes: [],
      bundles: [],
    },
  });
  // plans will be fetched once and stored here to be used in the server creation process
  const plans: { data: Plan[] } = reactive({
    data: [],
  });

  function setNestedProperty(obj: any, keys: string[], value: any) {
    let current = obj;
    for (let i = 0; i < keys.length - 1; i += 1) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
  }

  function setServerCreationState(keys: string[], value: any) {
    setNestedProperty(serverCreationState.data, keys, value);
  }

  // function resetServerCreationState(fullReset = false) {
  //   const providerId = configurables.data.providers.find((p) => p.code === generalAppVariables.DEFAULT_PROVIDER)?._id || '';
  //   const matchedPlan = plans.data.find((p: { code: string }) => p.code === generalAppVariables.DEFAULT_PAID_PLAN_GCP);
  //   const regionsByPlan = matchedPlan?.regions
  //     .filter((region) => region.status === FeatureStatus.AVAILABLE)
  //     .map((region) => ({ ...configurables.data.regions.find((cRegion: ConfigurableRegion) => cRegion._id === region.refId), ...region }))
  //     .sort((a, b) => a.order - b.order);

  //   const defaultDomain = configurables.data.features.find((f) => f.type === 'domain' && f.code === 'default-domain')?._id || '';
  //   const defaultRetention = configurables.data.features.find((f) => f.type === 'retention' && f.code === '6months')?._id || '';
  //   const defaultBackup = configurables.data.features.find((f) => f.type === 'backup' && f.code === 'daily')?._id || '';
  //   // @ts-ignore
  //   const defaultRegion = regionsByPlan[0]._id || '';
  //   const defaultName = 'My Countly Server';
  //   const defaultSize = matchedPlan?.sizes[0].refId || '';

  //   serverCreationState.data = {
  //     inProgress: serverCreationState.data.inProgress,
  //     isActiveServerFree: serverCreationState.data.isActiveServerFree,
  //     name: defaultName,
  //     subscription: {
  //       _id: '',
  //       planCode: matchedPlan?.code || '',
  //       planId: matchedPlan?._id || '',
  //       planConfig: {
  //         region: defaultRegion,
  //         features: [],
  //         size: defaultSize,
  //         provider: generalAppVariables.DEFAULT_PROVIDER,
  //       },
  //       specialFeatures: {
  //         domain: defaultDomain,
  //         backup: defaultBackup,
  //         retention: defaultRetention,
  //       },
  //       providerId,
  //     },
  //     activePaymentPeriod: PaymentPeriod.MONTH,
  //     pricing: {
  //       total: 0,
  //       currency: 'USD',
  //       isDiscountApplied: false,
  //       discountCode: '',
  //       discountedTotal: {
  //         subtotal: 0,
  //         tax: 0,
  //         total: 0,
  //         discount: 0,
  //         credit: 0,
  //         balance: 0,
  //         currency: 'USD',
  //       },
  //     },
  //     isLoading: false,
  //   };
  // }

  // Catalog features that belong to a given plan (joined by refId). Resolving selections
  // against THIS list — not the full catalog — guarantees the ids belong to the plan, so
  // legacy duplicates (with bare, unversioned codes) can never leak into the payload.
  function planCatalogFeatures(matchedPlan?: Plan): ConfigurableFeature[] {
    const refIds = new Set((matchedPlan?.features || []).map((pf) => pf.refId));
    return (configurables.data.features || []).filter((cf: ConfigurableFeature) => refIds.has(cf._id));
  }

  function resetServerCreationState(targetPlanCode?: string) {
    const providerId = configurables.data.providers.find((p) => p.code === generalAppVariables.DEFAULT_PROVIDER)?._id || '';
    const defaultPlanCode = targetPlanCode || generalAppVariables.DEFAULT_PAID_PLAN_GCP;
    const matchedPlan = plans.data.find((p: { code: string }) => p.code === defaultPlanCode);
    if (!matchedPlan) return;
    // Regions carry a versioned, v3-prefixed code (e.g. "v3_GCP_US_CENTRAL1_GCP").
    // The shared catalog still holds the legacy duplicates (e.g. "GCP_US-CENTRAL1_GCP"),
    // so filterV3 keeps the default region on the v3 namespace.
    const regionsByPlan = filterV3(matchedPlan?.regions
      .filter((region) => region.status === FeatureStatus.AVAILABLE)
      .map((region) => ({ ...configurables.data.regions.find((cRegion: ConfigurableRegion) => cRegion._id === region.refId), ...region })))
      .sort((a, b) => a.order - b.order);

    // Resolve special-feature defaults from THIS plan's own features (see planCatalogFeatures)
    // so the chosen ids always belong to the plan. Matching the raw catalog by bare code
    // would pick up legacy duplicates the plan doesn't include, which the backend rejects
    // with "Plan does contain some invalid resources".
    const planFeatures = planCatalogFeatures(matchedPlan);
    const findPlanFeatureId = (type: string, code: string) => planFeatures
      .find((f) => f.type === type && featureCodeMatches(f.code, code))?._id || '';

    const defaultDomain = findPlanFeatureId('domain', 'default-domain');
    const defaultRetention = findPlanFeatureId('retention', '6months');
    const defaultBackup = findPlanFeatureId('backup', 'daily');
    const defaultRegion = regionsByPlan?.[0]?._id || '';
    const defaultName = 'My Countly Server';
    const defaultSize = matchedPlan?.sizes[0].refId || '';

    serverCreationState.data = {
      inProgress: serverCreationState.data.inProgress,
      isActiveServerFree: serverCreationState.data.isActiveServerFree,
      name: defaultName,
      subscription: {
        _id: '',
        planCode: matchedPlan?.code || '',
        planId: matchedPlan?._id || '',
        planConfig: {
          region: defaultRegion,
          features: [],
          bundles: [],
          size: defaultSize,
          provider: generalAppVariables.DEFAULT_PROVIDER,
        },
        specialFeatures: {
          domain: defaultDomain,
          backup: defaultBackup,
          retention: defaultRetention,
        },
        providerId,
      },
      activePaymentPeriod: PaymentPeriod.MONTH,
      pricing: {
        total: 0,
        currency: 'USD',
        isDiscountApplied: false,
        discountCode: '',
        discountedTotal: {
          subtotal: 0,
          tax: 0,
          total: 0,
          discount: 0,
          credit: 0,
          balance: 0,
          currency: 'USD',
        },
      },
      isLoading: false,
      supportLevel: 'standard',
      oneTimeServices: [],
    };
  }

  function setConfigurables(value: any) {
    // Configurables are a shared catalog referenced by plans via refId. The catalog is
    // stored UNFILTERED on purpose: provider code is "gcp" (not v3-prefixed) so filtering
    // it would wipe the catalog, and legacy (v2-era) servers being edited still need to
    // resolve their old region/size by id for the summary. Regions/sizes/bundles DO carry
    // a v3-prefixed code and the backend returns legacy duplicates alongside them, so the
    // v3 filtering happens at the consumer (where plan.regions joins this catalog) via
    // filterV3 — not here — to avoid breaking edit-mode resolution of legacy ids.
    configurables.data = value;
  }

  function setPlans(value: any) {
    plans.data = filterV3<Plan>(value).filter((plan) => !isHiddenPlanCode(plan));
  }

  function setLoading(value: boolean) {
    serverCreationState.data.isLoading = value;
  }

  function getServerCreationPayload(isFree: boolean, company: string, project: string, isEdit = false) {
    const { planConfig } = serverCreationState.data.subscription;
    const baseFeatures = [
      ...(planConfig.features || []),
      serverCreationState.data.subscription.specialFeatures.domain,
      serverCreationState.data.subscription.specialFeatures.retention,
      serverCreationState.data.subscription.specialFeatures.backup,
    ].filter(Boolean);
    // Resolve support + one-time ids from THIS plan's own features (not the raw catalog):
    // v3 feature codes are version-prefixed ("v3_support-standard"), so a bare-code lookup
    // against the catalog finds nothing (silently dropping the selection) or hits a legacy
    // duplicate the plan rejects. Scoping to the plan keeps the ids valid and present.
    const matchedPlan = plans.data.find((p) => p.code === serverCreationState.data.subscription.planCode);
    const planFeatures = planCatalogFeatures(matchedPlan);
    // Support: include selected support level (standard or dedicated) so it appears after purchase
    const supportCode = serverCreationState.data.supportLevel === 'dedicated' ? 'support-dedicated' : 'support-standard';
    const supportId = planFeatures.find((f) => featureCodeMatches(f.code, supportCode))?._id;
    // One-time services: include selected one-time service feature IDs
    const oneTimeIds = (serverCreationState.data.oneTimeServices || [])
      .map((code: string) => planFeatures.find((f) => featureCodeMatches(f.code, code))?._id)
      .filter(Boolean) as string[];
    const features: string[] = [...new Set([...baseFeatures, ...(supportId ? [supportId] : []), ...oneTimeIds])].filter(Boolean) as string[];
    const payload: ServerCreationPayload = {
      name: serverCreationState.data.name,
      paymentCycle: serverCreationState.data.activePaymentPeriod as PaymentPeriod,
      planConfig: {
        region: planConfig.region,
        size: planConfig.size,
        features,
        ...(planConfig.bundles && planConfig.bundles.length > 0 ? { bundles: planConfig.bundles } : {}),
      },
      plan: serverCreationState.data.subscription.planId,
      payment: {
        vendor: isFree ? PaymentVendor.FREE : PaymentVendor.PADDLE,
      },
    };

    if (isEdit) {
      payload.planCode = serverCreationState.data.subscription.planCode;
    } else {
      payload.company = company;
      payload.project = project;
      payload.provider = serverCreationState.data.subscription.planConfig.provider;
      payload.planConfig.provider = serverCreationState.data.subscription.providerId;
    }

    return payload;
  }

  return {
    configurables,
    plans,
    serverCreationState,
    setServerCreationState,
    setConfigurables,
    setPlans,
    resetServerCreationState,
    getServerCreationPayload,
    setLoading,
  };
});
